/**
 * Black Matte VTC - Partner API Service
 * All Partner-specific API calls
 * NO business logic - just API communication
 */

import { partnerApiClient as apiClient } from './apiClient';

const PARTNER_TOKEN_KEY = 'partner_token';

class PartnerService {
  // =========================================================================
  // TOKEN MANAGEMENT
  // =========================================================================
  
  getToken() {
    return localStorage.getItem(PARTNER_TOKEN_KEY);
  }
  
  setToken(token) {
    localStorage.setItem(PARTNER_TOKEN_KEY, token);
  }
  
  clearToken() {
    localStorage.removeItem(PARTNER_TOKEN_KEY);
  }
  
  getAuthHeaders() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
  
  // =========================================================================
  // AUTHENTICATION
  // =========================================================================
  
  async login(email, password) {
    const response = await apiClient.post('/api/partner/auth/login', {
      email,
      password
    });
    
    if (response.access_token) {
      this.setToken(response.access_token);
    }
    
    return response;
  }
  
  async logout() {
    this.clearToken();
  }
  
  async getProfile() {
    return apiClient.get('/api/partner/me', {
      headers: this.getAuthHeaders()
    });
  }
  
  // =========================================================================
  // DASHBOARD
  // =========================================================================
  
  async getDashboard() {
    return apiClient.get('/api/partner/dashboard', {
      headers: this.getAuthHeaders()
    });
  }
  
  // =========================================================================
  // BALANCE & BILLING
  // =========================================================================
  
  async getBalance() {
    return apiClient.get('/api/partner/billing/balance', {
      headers: this.getAuthHeaders()
    });
  }
  
  async getLedger(limit = 50) {
    return apiClient.get(`/api/partner/billing/ledger?limit=${limit}`, {
      headers: this.getAuthHeaders()
    });
  }
  
  // =========================================================================
  // BOOKINGS
  // =========================================================================
  
  async getBookings(limit = 50, status = null) {
    let url = `/api/partner/bookings?limit=${limit}`;
    if (status) {
      url += `&status=${status}`;
    }
    return apiClient.get(url, {
      headers: this.getAuthHeaders()
    });
  }
  
  async getBooking(bookingId) {
    return apiClient.get(`/api/partner/bookings/${bookingId}`, {
      headers: this.getAuthHeaders()
    });
  }
  
  async createBooking(bookingData) {
    return apiClient.post('/api/partner/bookings', bookingData, {
      headers: this.getAuthHeaders()
    });
  }
  
  async cancelBooking(bookingId, reason) {
    return apiClient.post(`/api/partner/bookings/${bookingId}/cancel`, 
      { reason },
      { headers: this.getAuthHeaders() }
    );
  }
  
  // =========================================================================
  // INVOICES
  // =========================================================================
  
  async getInvoices(limit = 50, status = null) {
    let url = `/api/partner/billing/invoices?limit=${limit}`;
    if (status) {
      url += `&status=${status}`;
    }
    return apiClient.get(url, {
      headers: this.getAuthHeaders()
    });
  }
  
  async getInvoice(invoiceId) {
    return apiClient.get(`/api/partner/billing/invoices/${invoiceId}`, {
      headers: this.getAuthHeaders()
    });
  }
  
  // =========================================================================
  // BOOKING DETAIL
  // =========================================================================
  
  async getBookingEvents(bookingId) {
    return apiClient.get(`/api/partner/bookings/${bookingId}/events`, {
      headers: this.getAuthHeaders()
    });
  }
  
  // =========================================================================
  // PRICE ESTIMATE
  // =========================================================================
  
  async getEstimate(pickup, destination) {
    return apiClient.post('/api/partner/estimate', 
      { pickup_address: pickup, destination_address: destination },
      { headers: this.getAuthHeaders() }
    );
  }
}

export const partnerService = new PartnerService();
export default partnerService;
